import React, {useState, useEffect} from 'react';
import {
  Avatar,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
} from '@mui/material';
import {
  Chip,
  DateTimePicker,
  Input,
  RadioGroups,
  Select,
} from '../../../../../components';
import {Typography} from './ApproveReceiptForm.style';
import IslanderPartnerService from '../../../../../services/Islander/islander.service';
import {DataDetail} from '../../../../../types/typeAllTransaction';
import {
  envRole,
  formatDate,
  formatDateTime,
  getEndOfDate,
  getStartOfDate,
  hasPermission,
  formatLocalDateFromUTCTime,
} from '../../../../../utils';
import {Box} from '@mui/system';
import {forEach, isEmpty, isFunction} from 'lodash';
import {images} from '../../../../../constants/image';
import colors from '../../../../../theme/colors';
import {
  DEFAULT_ERROR_INPUT_FIELDS,
  DEFAULT_INITIAL_VALUES,
  DEFAULT_KEYS_SELECTED,
  DEFAULT_PARTIAL_POINTS_CHECKBOXES,
  NON_PARTICIPATING_OUTLET_LABEL,
} from '../../../../../constants/transaction';

interface InitialValues {
  oCRHistoryReceiptId?: string | undefined;
  receiptDateOfPurchase?: string | undefined;
  receiptTotal?: number | undefined;
  matchedMerchantID?: number | undefined;
  receiptName?: string | undefined;
  receiptAddress?: string | undefined;
  pointValue?: number | undefined;
  associatedPointValue?: number | undefined;
  islanderPartnerName?: string | undefined;
  merchantReceiptId?: string | number | undefined;
  receiptOperatingArea?: string | null | undefined;
}

interface ApproveReceiptFormProps {
  transactionDetail?: DataDetail;
  setInitialValues: (params: any) => void;
  initialValues: InitialValues;
  setYesNoAmend: (any) => void;
  yesNoAmend: any;
  setKeysSelected: (any) => void;
  keysSelected: any;
  receiptAddressValue: any;
  receiptNameValue: any;
  setReceiptNameValue: Function;
  setReceiptAddressValue: Function;
  receiptOperatingAreaValue: any;
  setReceiptOperatingAreaValue: Function;
  errorInputFields?: any;
  setErrorInputFields?: (params: any) => void;
  formError?: string;
  validationField?: Function;
  receiptDetail?: any;
  memberPointsSummary?: any;
  partialPointsCheckboxes?: any;
  setPartialPointsCheckboxes?: Function;
  selectedMerchantPointMultiplierEvents?: any;
  formErrorRef?: React.RefObject<HTMLDivElement>;
}

const ModalInfoBox = ({children}) => (
  <Grid
    item
    key="infos-box"
    sm={12}
    sx={theme => ({
      '--spacing-y': theme.spacing(6),
      '--spacing-x': theme.spacing(4),
      '--border-radius': theme.spacing(2),
      border: `solid 1px ${colors.grey[300]}`,
      borderRadius: `var(--border-radius, ${theme.spacing(2)})`,
      py: `var(--spacing-y, ${theme.spacing(6)})`,
      px: `var(--spacing-x, ${theme.spacing(4)})`,
      '& .MuiFormControl-root': {
        mb: 0,
        '& + .MuiFormControl-root': {
          mt: 8,
        },
      },
    })}
  >
    <Stack>{children}</Stack>
  </Grid>
);

const ApproveReceiptForm = ({
  transactionDetail,
  initialValues,
  setInitialValues,
  yesNoAmend,
  setYesNoAmend,
  keysSelected,
  setKeysSelected,
  receiptNameValue,
  setReceiptNameValue,
  receiptAddressValue,
  setReceiptAddressValue,
  receiptOperatingAreaValue,
  setReceiptOperatingAreaValue,
  errorInputFields,
  setErrorInputFields,
  formError,
  validationField,
  receiptDetail,
  memberPointsSummary,
  partialPointsCheckboxes,
  setPartialPointsCheckboxes,
  selectedMerchantPointMultiplierEvents,
  formErrorRef,
}: ApproveReceiptFormProps) => {
  const localFormat = 'DD MMM YYYY hh:mmA';
  const [listMerchant, setListMerchant] = useState<any>([]);
  const [islanderPartnerNames, setIslanderPartnerNames] = useState<any>([]);
  const [islanderPartnerAddress, setIslanderPartnerAddress] = useState<any>([]);
  const [islanderPartnerOperatingArea, setIslanderPartnerOperatingArea] =
    useState<any>([]);
  const yesNoOptions = [
    {key: 'Yes', value: true},
    {
      key: 'No',
      value: false,
    },
  ];
  const isBonusPointsReadOnly =
    transactionDetail?.pointMultiplierEventId &&
    keysSelected.receiptPartnerAndAddress === true &&
    initialValues.merchantReceiptId &&
    selectedMerchantPointMultiplierEvents.length === 0;

  const handleChangeKeysSelected = (value, key) => {
    setKeysSelected({
      ...keysSelected,
      [key]: value,
    });
    // If value is false, that means user just uncheck the checkbox
    // => clean the value of the field unther the checkbox group
    // => then update initialValues
    if (value === false) {
      const updatedValues = {};
      const checkboxesUpdatedValues = {};
      const formatInitialValuesGroup = {
        oCRHistoryReceiptId: {
          oCRHistoryReceiptId: DEFAULT_INITIAL_VALUES.oCRHistoryReceiptId,
        },
        receiptDateOfPurchase: {
          receiptDateOfPurchase: DEFAULT_INITIAL_VALUES.receiptDateOfPurchase,
        },
        receiptTotal: {
          receiptTotal: DEFAULT_INITIAL_VALUES.receiptTotal,
        },
        pointValue: {
          pointValue: DEFAULT_INITIAL_VALUES.pointValue,
          associatedPointValue: DEFAULT_INITIAL_VALUES.associatedPointValue,
        },
        receiptPartnerAndAddress: {
          matchedMerchantID: DEFAULT_INITIAL_VALUES.matchedMerchantID,
          merchantReceiptId: DEFAULT_INITIAL_VALUES.merchantReceiptId,
          receiptName: DEFAULT_INITIAL_VALUES.receiptName,
          receiptAddress: DEFAULT_INITIAL_VALUES.receiptAddress,
          receiptOperatingArea: DEFAULT_INITIAL_VALUES.receiptOperatingArea,
        },
      };
      const checkboxesGroup = {
        pointValue: {
          pointValue: DEFAULT_PARTIAL_POINTS_CHECKBOXES.pointValue,
          associatedPointValue:
            DEFAULT_PARTIAL_POINTS_CHECKBOXES.associatedPointValue,
        },
      };
      if (formatInitialValuesGroup[key]) {
        forEach(formatInitialValuesGroup[key], (v, k) => {
          updatedValues[k] = v;
        });
      }
      if (checkboxesGroup[key]) {
        forEach(checkboxesGroup[key], (v, k) => {
          checkboxesUpdatedValues[k] = v;
        });
      }
      if (!isEmpty(updatedValues)) {
        setInitialValues({...initialValues, ...updatedValues});
      }
      if (
        !isEmpty(checkboxesUpdatedValues) &&
        isFunction(setPartialPointsCheckboxes)
      ) {
        setPartialPointsCheckboxes({
          ...partialPointsCheckboxes,
          ...checkboxesUpdatedValues,
        });
      }
    }
  };

  const handleKeyDown = (event: any, inputKey: any) => {
    if (
      !['receiptTotal', 'pointValue', 'associatedPointValue'].includes(inputKey)
    ) {
      return;
    }
    // 1. Define allowed keys: digits, backspace, delete, tab, arrow keys, '.' (if decimals allowed)
    // 2. Define disallowed keys specifically for type="number" quirks if still using it
    // 3. Allow special control keys (Ctrl/Cmd + A, C, V, X, Z)

    const {key, ctrlKey, metaKey} = event; // metaKey is Cmd on Mac

    // Allow navigation, editing commands (Cmd/Ctrl + A/C/V/X/Z)
    if (ctrlKey || metaKey) {
      if (['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase())) {
        return; // Allow these combinations
      }
    }

    // Allow Backspace, Delete, Tab, Escape, Enter, Arrow keys
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ].includes(key)
    ) {
      return;
    }

    // --- Specific logic for strictly numeric input ---
    // If you want to allow a negative sign at the beginning, handle it.
    // If you want to allow a decimal point, handle it.

    // Allow only digits (0-9)
    if (!/^[0-9]$/.test(key)) {
      // Allow a single decimal point
      if (key === '.') {
        // Prevent multiple decimal points
        if (initialValues[inputKey]?.includes('.')) {
          event.preventDefault();
        }
        return;
      }

      // Prevent all other non-numeric keys
      event.preventDefault();
    }
  };

  const handleChangeValue = (value, key) => {
    if (key === 'matchedMerchantID') {
      const islanderPartnerNamesOptions = getMerchantNamesOptions(value);
      setInitialValues({
        ...initialValues,
        [key]: value,
        receiptName: DEFAULT_INITIAL_VALUES.receiptName,
        merchantReceiptId: DEFAULT_INITIAL_VALUES.merchantReceiptId,
        receiptAddress: DEFAULT_INITIAL_VALUES.receiptAddress,
        receiptOperatingArea: DEFAULT_INITIAL_VALUES.receiptOperatingArea,
      });
      setIslanderPartnerNames(islanderPartnerNamesOptions);
      setIslanderPartnerAddress([]);
      setIslanderPartnerOperatingArea([]);
      setErrorInputFields({
        ...errorInputFields,
        [key]: DEFAULT_ERROR_INPUT_FIELDS[key],
        merchantReceiptId: DEFAULT_ERROR_INPUT_FIELDS.merchantReceiptId,
        receiptName: DEFAULT_ERROR_INPUT_FIELDS.receiptName,
        receiptAddress: DEFAULT_ERROR_INPUT_FIELDS.receiptAddress,
        receiptOperatingArea: DEFAULT_ERROR_INPUT_FIELDS.receiptOperatingArea,
      });
      setReceiptNameValue(DEFAULT_INITIAL_VALUES.receiptName);
      setReceiptAddressValue(DEFAULT_INITIAL_VALUES.receiptAddress);
      setReceiptOperatingAreaValue(DEFAULT_INITIAL_VALUES.receiptOperatingArea);
    } else if (key === 'receiptName') {
      const islanderPartnerAddressOptions = getMerchantAddressOptions(value);
      setInitialValues({
        ...initialValues,
        [key]: value,
        receiptAddress: DEFAULT_INITIAL_VALUES.receiptAddress,
        receiptOperatingArea: DEFAULT_INITIAL_VALUES.receiptOperatingArea,
      });
      setIslanderPartnerAddress(islanderPartnerAddressOptions);
      setIslanderPartnerOperatingArea([]);
      setErrorInputFields({
        ...errorInputFields,
        [key]: DEFAULT_ERROR_INPUT_FIELDS[key],
        receiptAddress: DEFAULT_ERROR_INPUT_FIELDS.receiptAddress,
        receiptOperatingArea: DEFAULT_ERROR_INPUT_FIELDS.receiptOperatingArea,
      });
      setReceiptNameValue(value);
      setReceiptAddressValue(DEFAULT_INITIAL_VALUES.receiptAddress);
      setReceiptOperatingAreaValue(DEFAULT_INITIAL_VALUES.receiptOperatingArea);
    } else if (key === 'receiptAddress') {
      const islanderPartnerOperatingAreaOptions =
        getMerchantOperatingAreaOptions(value);
      setInitialValues({
        ...initialValues,
        [key]: value,
        receiptOperatingAreaValue: DEFAULT_INITIAL_VALUES.receiptOperatingArea,
      });
      setReceiptAddressValue(value);
      setIslanderPartnerOperatingArea(islanderPartnerOperatingAreaOptions);
      setReceiptOperatingAreaValue(DEFAULT_INITIAL_VALUES.receiptOperatingArea);
      setErrorInputFields({
        ...errorInputFields,
        [key]: DEFAULT_ERROR_INPUT_FIELDS[key],
        receiptOperatingArea: DEFAULT_ERROR_INPUT_FIELDS.receiptOperatingArea,
      });
    } else if (key === 'receiptOperatingArea') {
      setInitialValues({
        ...initialValues,
        [key]: value,
      });
      setReceiptOperatingAreaValue(value);
      setErrorInputFields({
        ...errorInputFields,
        [key]: DEFAULT_ERROR_INPUT_FIELDS[key],
      });
    } else {
      setInitialValues({...initialValues, [key]: value});
      let errorMessage = DEFAULT_ERROR_INPUT_FIELDS[key] || '';
      let partialPointsCheckboxesObject = {};
      if (isFunction(validationField)) {
        const validation = validationField(key, value);
        errorMessage = validation.errorsObject[key];
        partialPointsCheckboxesObject =
          validation.partialPointsCheckboxesObject;
      }
      setErrorInputFields({...errorInputFields, [key]: errorMessage});
      if (
        !isEmpty(partialPointsCheckboxesObject) &&
        isFunction(setPartialPointsCheckboxes)
      ) {
        setPartialPointsCheckboxes({
          ...partialPointsCheckboxes,
          ...partialPointsCheckboxesObject,
        });
      }
    }
  };

  // Filter unique list receipt name from selected merchant
  const getMerchantNamesOptions = value => {
    const selectedMerchant = listMerchant.find(
      merchant => merchant.value === value
    );
    if (selectedMerchant?.data?.merchantReceipts?.length > 0) {
      return selectedMerchant?.data?.merchantReceipts?.reduce(
        (acc, merchant) => {
          const receiptName = merchant.receiptName;
          const isReceiptNameExist = acc.find(
            receipt => receipt.value === receiptName
          );
          if (!isReceiptNameExist) {
            acc.push({
              label: merchant.receiptName,
              value: merchant.receiptName,
            });
          }
          return acc;
        },
        []
      );
    }
    return [];
  };

  const getMerchantAddressOptions = value => {
    const selectedMerchant = listMerchant.find(
      merchant => merchant.value === initialValues.matchedMerchantID
    );
    const listAdresses =
      selectedMerchant?.data?.merchantReceipts?.filter(
        merchantReceipt => merchantReceipt.receiptName === value
      ) || [];
    if (listAdresses.length > 0) {
      return listAdresses.reduce((acc, merchantReceipt) => {
        const originReceiptAddressValue = ['', null].includes(
          merchantReceipt.receiptAddress
        )
          ? '--'
          : merchantReceipt.receiptAddress;
        const receiptAddressLabel = originReceiptAddressValue ?? '--';
        const isReceiptAddressExist = acc.find(
          receipt => receipt.value === originReceiptAddressValue
        );
        if (!isReceiptAddressExist) {
          acc.push({
            label: receiptAddressLabel,
            value: originReceiptAddressValue,
          });
        }
        return acc;
      }, []);
    }
    return [];
  };

  const getMerchantOperatingAreaOptions = value => {
    const selectedMerchant = listMerchant.find(
      merchant => merchant.value === initialValues.matchedMerchantID
    );
    const listOperatingAreas =
      selectedMerchant?.data?.merchantReceipts?.filter(merchantReceipt => {
        const matchedReceiptName =
          merchantReceipt.receiptName === initialValues.receiptName;
        const matchedReceiptAddress =
          value === '--'
            ? ['', null].includes(merchantReceipt.receiptAddress)
            : merchantReceipt.receiptAddress === value;
        return matchedReceiptName && matchedReceiptAddress;
      }) || [];
    if (listOperatingAreas.length > 0) {
      return listOperatingAreas.reduce((acc, merchantReceipt) => {
        const originReceiptOperatingAreaValue = ['', null].includes(
          merchantReceipt.receiptOperatingArea
        )
          ? '--'
          : merchantReceipt.receiptOperatingArea;
        const receiptOperatingAreaLabel =
          originReceiptOperatingAreaValue ?? '--';
        const isReceiptOperatingAreaExist = acc.find(
          receipt => receipt.value === originReceiptOperatingAreaValue
        );
        if (!isReceiptOperatingAreaExist) {
          acc.push({
            label: receiptOperatingAreaLabel,
            value: originReceiptOperatingAreaValue,
          });
        }
        return acc;
      }, []);
    }
    return [];
  };

  const resetForm = () => {
    setKeysSelected(DEFAULT_KEYS_SELECTED);
    setIslanderPartnerAddress([]);
    setIslanderPartnerOperatingArea([]);
    setInitialValues(DEFAULT_INITIAL_VALUES);
  };

  const getListMerchant = () => {
    new IslanderPartnerService()
      .get('get-filter-data', {
        isAvailableMerchantReceipt_eq: 1,
      })
      .then(res => {
        if (res?.data?.data?.length > 0) {
          setListMerchant(
            res?.data?.data?.reduce((acc: any, merchant: any) => {
              acc.push({
                label: merchant.name,
                value: merchant.id,
                data: merchant,
              });
              return acc;
            }, [])
          );
        }
      });
  };

  const renderValue = (value, defaultValue: any = '--', config: any = {}) => {
    const {wrapInTag = true, tagStyles = {}} = config || {};
    const isNullOrEmpty = value === null || value === '' || value === undefined;
    const localValue = !isNullOrEmpty ? value : defaultValue;
    if (wrapInTag === false) {
      return localValue;
    }
    return (
      <span style={{fontWeight: 'bolder', display: 'block', ...tagStyles}}>
        {localValue}
      </span>
    );
  };

  const renderLabelText = (label: string, config?: any) => {
    const {sx, ...localLabelConfig} = config ?? {};
    return (
      <Typography
        component="span"
        fontWeight="bolder"
        sx={{
          mb: 2,
          ...sx,
        }}
        {...localLabelConfig}
      >
        {label}
      </Typography>
    );
  };

  const renderLabelTextWithValue = (
    label: string,
    value: any,
    config: any = {}
  ) => {
    const {
      labelConfig = null,
      valueConfig = null,
      renderValueConfig = {
        defaultValue: '--',
        wrapInTag: false,
        tagStyles: {
          fontWeight: 'normal',
        },
      },
    } = config || {};
    const localLabelConfig = labelConfig ?? {
      sx: {
        mr: 2,
        mb: 0,
      },
    };
    const localValueConfig = valueConfig ?? {};
    const {defaultValue, ...renderValueConfigRest} = renderValueConfig || {};
    return (
      <Box sx={{mb: 4}}>
        {renderLabelText(label, localLabelConfig)}
        <Typography component="span" {...localValueConfig}>
          {renderValue(value, defaultValue, renderValueConfigRest)}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    if (yesNoAmend === 'false') {
      resetForm();
    }
  }, [yesNoAmend]);

  useEffect(() => {
    getListMerchant();
  }, []);

  useEffect(() => {
    if (keysSelected.receiptPartnerAndAddress === false) {
      setReceiptAddressValue('');
      setReceiptOperatingAreaValue('');
      setIslanderPartnerAddress([]);
      setIslanderPartnerOperatingArea([]);
    }
  }, [keysSelected]);

  useEffect(() => {
    const isReceiptNameChanged =
      initialValues.receiptName !== DEFAULT_INITIAL_VALUES.receiptName;
    const isReceiptAddressChanged =
      initialValues.receiptAddress !== DEFAULT_INITIAL_VALUES.receiptAddress;
    const isReceiptAddressNotChangedButReceiptAddressEmpty =
      initialValues.receiptAddress === DEFAULT_INITIAL_VALUES.receiptAddress &&
      islanderPartnerAddress.length === 1 &&
      islanderPartnerAddress[0].value === '--' &&
      receiptAddressValue !== '';
    const isMatchedMerchantIDChanged =
      initialValues.matchedMerchantID !==
      DEFAULT_INITIAL_VALUES.matchedMerchantID;
    const isReceiptOperatingAreaChanged =
      initialValues.receiptOperatingArea !==
      DEFAULT_INITIAL_VALUES.receiptOperatingArea;
    const isReceiptOperatingAreaNotChangedButOperatingAreaEmpty =
      initialValues.receiptOperatingArea ===
        DEFAULT_INITIAL_VALUES.receiptOperatingArea &&
      islanderPartnerOperatingArea.length === 1 &&
      islanderPartnerOperatingArea[0].value === '--' &&
      receiptOperatingAreaValue !== '';
    const listMerchantNotEmpty = listMerchant?.length > 0;
    if (
      isReceiptNameChanged &&
      (isReceiptAddressChanged ||
        isReceiptAddressNotChangedButReceiptAddressEmpty) &&
      isMatchedMerchantIDChanged &&
      (isReceiptOperatingAreaChanged ||
        isReceiptOperatingAreaNotChangedButOperatingAreaEmpty) &&
      listMerchantNotEmpty
    ) {
      const selectedMerchant = listMerchant.find(
        merchant => merchant.value === initialValues.matchedMerchantID
      );
      const selectedMerchantReceipt =
        selectedMerchant?.data?.merchantReceipts?.find(merchantReceipt => {
          const matchedReceiptName =
            merchantReceipt.receiptName === initialValues.receiptName;
          const matchedReceiptAddress =
            initialValues.receiptAddress === '--'
              ? [null, ''].includes(merchantReceipt.receiptAddress)
              : merchantReceipt.receiptAddress === initialValues.receiptAddress;
          const matchedReceiptOperatingArea =
            initialValues.receiptOperatingArea === '--'
              ? [null, ''].includes(merchantReceipt.receiptOperatingArea)
              : merchantReceipt.receiptOperatingArea ===
                initialValues.receiptOperatingArea;
          return (
            matchedReceiptName &&
            matchedReceiptAddress &&
            matchedReceiptOperatingArea
          );
        });
      if (
        selectedMerchantReceipt &&
        selectedMerchantReceipt.id !== initialValues.merchantReceiptId
      ) {
        setInitialValues({
          ...initialValues,
          merchantReceiptId: selectedMerchantReceipt.id,
        });
      }
    } else if (initialValues.merchantReceiptId) {
      setInitialValues({
        ...initialValues,
        merchantReceiptId: DEFAULT_INITIAL_VALUES.merchantReceiptId,
      });
    }
  }, [
    initialValues,
    listMerchant,
    islanderPartnerOperatingArea,
    receiptOperatingAreaValue,
  ]);

  const getTransactionId = () => {
    const isWorkflowIdNull = transactionDetail?.workflowId === null;
    const mainTransactionId = isWorkflowIdNull
      ? transactionDetail?.id
      : transactionDetail?.associatedPointId;
    const bonusTransactionId = isWorkflowIdNull
      ? transactionDetail?.associatedPointId
      : transactionDetail?.id;
    return {mainTransactionId, bonusTransactionId};
  };

  const renderIslanderPartnerNote = () => {
    let note;
    if (!initialValues.matchedMerchantID) {
      return null;
    }
    const selectedMerchantHasPointMultiplier =
      selectedMerchantPointMultiplierEvents.length > 0;
    const currentTransactionHasBonusTransaction =
      transactionDetail?.pointMultiplierEventId;
    if (
      selectedMerchantHasPointMultiplier &&
      !currentTransactionHasBonusTransaction
    ) {
      note =
        'Note: The Island Partner you have selected is applicable for bonus points. Once you submit, we will automatically create a transaction for bonus points to be credited.';
    }
    if (
      !selectedMerchantHasPointMultiplier &&
      currentTransactionHasBonusTransaction
    ) {
      note =
        'Note: The Island Partner you have selected is NOT applicable for bonus points. Bonus points will NOT be credited to the member once you amend this transaction.';
    }
    if (note) {
      return (
        <Box
          sx={{
            mt: 4,
            px: 4,
            py: 2,
            borderRadius: 2,
            backgroundColor: colors.grey[200],
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {note}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const getKeysSelectedValue = (item: any) => {
    return item?.value === true || item === true;
  };

  return (
    <Grid container>
      <Grid item key="yesNo-box" sm={12}>
        <FormControl fullWidth margin="normal">
          <Typography variant="body2" mb={2} fontWeight="bolder">
            Is there anything you need to amend?
          </Typography>
          <RadioGroups
            name="yesNoAmend"
            options={yesNoOptions}
            values={yesNoAmend}
            onChange={e => {
              setYesNoAmend(e.target.value);
            }}
          />
        </FormControl>
      </Grid>
      {yesNoAmend === 'true' && (
        <Grid item key="select-field-box" sm={12}>
          <FormControl fullWidth margin="normal">
            <Typography variant="body2" mb={2} fontWeight="bolder">
              Select field(s) you would like to amend *
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  value={getKeysSelectedValue(
                    keysSelected['oCRHistoryReceiptId']
                  )}
                  checked={getKeysSelectedValue(
                    keysSelected['oCRHistoryReceiptId']
                  )}
                  onChange={(e, value) =>
                    keysSelected['oCRHistoryReceiptId']?.type === 'force'
                      ? undefined
                      : handleChangeKeysSelected(value, 'oCRHistoryReceiptId')
                  }
                  inputProps={{'aria-label': 'controlled'}}
                />
              }
              label="Receipt/Order number"
            />
            {getKeysSelectedValue(keysSelected['oCRHistoryReceiptId']) && (
              <ModalInfoBox>
                <FormControl fullWidth>
                  {renderLabelTextWithValue(
                    'Receipt/Order number detected:',
                    receiptDetail?.receiptId
                  )}
                  {renderLabelText('New receipt/order number')}
                  <Input
                    type="text"
                    name="oCRHistoryReceiptId"
                    placeholder="Enter new receipt/order number"
                    helperText={errorInputFields.oCRHistoryReceiptId}
                    error={errorInputFields.oCRHistoryReceiptId !== ''}
                    size="small"
                    variant="outlined"
                    value={initialValues.oCRHistoryReceiptId}
                    onChange={e =>
                      handleChangeValue(e.target.value, 'oCRHistoryReceiptId')
                    }
                  />
                </FormControl>
              </ModalInfoBox>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  value={getKeysSelectedValue(
                    keysSelected['receiptDateOfPurchase']
                  )}
                  checked={getKeysSelectedValue(
                    keysSelected['receiptDateOfPurchase']
                  )}
                  onChange={(e, value) =>
                    keysSelected['receiptDateOfPurchase']?.type === 'force'
                      ? undefined
                      : handleChangeKeysSelected(value, 'receiptDateOfPurchase')
                  }
                  inputProps={{'aria-label': 'controlled'}}
                />
              }
              label="Receipt date and time"
            />
            {getKeysSelectedValue(keysSelected['receiptDateOfPurchase']) && (
              <ModalInfoBox>
                <FormControl fullWidth>
                  {renderLabelTextWithValue(
                    'Receipt date and time detected:',
                    formatDate(
                      receiptDetail?.receiptDateOfPurchase,
                      localFormat
                    )
                  )}
                  {renderLabelText('New receipt date and time')}
                  <DateTimePicker
                    value={initialValues.receiptDateOfPurchase}
                    helperText={errorInputFields.receiptDateOfPurchase}
                    isErrorMessage={
                      errorInputFields.receiptDateOfPurchase !== ''
                    }
                    onChange={e => {
                      handleChangeValue(e, 'receiptDateOfPurchase');
                    }}
                    placeholder="Select new receipt date and time"
                  />
                </FormControl>
              </ModalInfoBox>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  value={getKeysSelectedValue(keysSelected['receiptTotal'])}
                  checked={getKeysSelectedValue(keysSelected['receiptTotal'])}
                  onChange={(e, value) =>
                    keysSelected['receiptTotal']?.type === 'force'
                      ? undefined
                      : handleChangeKeysSelected(value, 'receiptTotal')
                  }
                  inputProps={{'aria-label': 'controlled'}}
                />
              }
              label="Total amount"
            />
            {getKeysSelectedValue(keysSelected['receiptTotal']) && (
              <ModalInfoBox>
                <FormControl fullWidth>
                  {renderLabelTextWithValue(
                    'Total amount detected',
                    receiptDetail?.receiptTotal !== null
                      ? '$' + receiptDetail?.receiptTotal
                      : null
                  )}
                  {renderLabelText('New total amount')}
                  <Input
                    type="text"
                    name="receiptTotal"
                    placeholder="Enter new total amount"
                    helperText={errorInputFields.receiptTotal}
                    error={errorInputFields.receiptTotal !== ''}
                    size="small"
                    variant="outlined"
                    value={initialValues.receiptTotal}
                    onChange={e =>
                      handleChangeValue(e.target.value, 'receiptTotal')
                    }
                    inputProps={{
                      min: 0,
                      inputMode: 'numeric',
                      onKeyDown: e => handleKeyDown(e, 'receiptTotal'),
                    }}
                  />
                </FormControl>
              </ModalInfoBox>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  value={getKeysSelectedValue(keysSelected['pointValue'])}
                  checked={getKeysSelectedValue(keysSelected['pointValue'])}
                  onChange={(e, value) =>
                    keysSelected['pointValue']?.type === 'force'
                      ? undefined
                      : handleChangeKeysSelected(value, 'pointValue')
                  }
                  inputProps={{'aria-label': 'controlled'}}
                />
              }
              label="Points earned"
            />
            {getKeysSelectedValue(keysSelected['pointValue']) && (
              <>
                {transactionDetail?.pointMultiplierEventId && (
                  <Box
                    sx={{
                      backgroundColor: '#F1F1F1',
                      borderRadius: 2,
                      py: 2,
                      px: 4,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#000000',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}
                    >
                      Note: This is a {transactionDetail?.pointsMultiplier}X
                      points transaction.
                    </Typography>
                  </Box>
                )}
                <ModalInfoBox>
                  {transactionDetail?.pointMultiplierEventId && (
                    <Chip
                      label={`MAIN TRANSACTION - # ${
                        getTransactionId()?.mainTransactionId
                      }`}
                      sx={{
                        mb: 4,
                        p: 0,
                        color: '#FFF',
                        backgroundColor: '#979797',
                        fontWeight: 'bold',
                        alignSelf: 'flex-start',
                        '& .MuiChip-label': {
                          fontSize: 10,
                          fontWeight: 'bold',
                          lineHeight: 1.4,
                          px: 4,
                          py: 1,
                        },
                      }}
                    />
                  )}
                  {renderLabelTextWithValue(
                    'Initial pending points:',
                    transactionDetail?.pointValue
                  )}
                  {renderLabelText('Adjusted points earned')}

                  <FormControl fullWidth>
                    <Input
                      type="text"
                      name="pointValue"
                      placeholder="Enter adjusted points earned"
                      helperText={errorInputFields.pointValue}
                      error={errorInputFields.pointValue !== ''}
                      size="small"
                      variant="outlined"
                      value={initialValues.pointValue}
                      onChange={e =>
                        handleChangeValue(e.target.value, 'pointValue')
                      }
                      inputProps={{
                        min: 0,
                        inputMode: 'numeric',
                        onKeyDown: e => handleKeyDown(e, 'pointValue'),
                      }}
                    />
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={partialPointsCheckboxes.pointValue}
                        onChange={(e, value) => {}}
                        inputProps={{'aria-label': 'controlled'}}
                      />
                    }
                    label="Partial points / Maximum daily points achieved"
                  />
                  {transactionDetail?.pointMultiplierEventId && (
                    <Box
                      sx={{
                        opacity: isBonusPointsReadOnly ? 0.5 : 1,
                      }}
                    >
                      <FormControl fullWidth>
                        <Chip
                          label={`BONUS POINTS - # ${
                            getTransactionId()?.bonusTransactionId
                          }`}
                          sx={{
                            mb: 4,
                            p: 0,
                            color: '#FFF',
                            backgroundColor: '#979797',
                            fontWeight: 'bold',
                            alignSelf: 'flex-start',
                            '& .MuiChip-label': {
                              fontSize: 10,
                              fontWeight: 'bold',
                              lineHeight: 1.4,
                              px: 4,
                              py: 1,
                            },
                          }}
                        />
                      </FormControl>
                      {renderLabelTextWithValue(
                        'Initial pending points:',
                        transactionDetail?.associatedPointValue
                      )}
                      {renderLabelText('Adjusted points earned')}
                      <FormControl fullWidth>
                        <Input
                          type="text"
                          name="associatedPointValue"
                          placeholder="Enter adjusted points earned"
                          helperText={errorInputFields.associatedPointValue}
                          error={errorInputFields.associatedPointValue !== ''}
                          size="small"
                          variant="outlined"
                          value={initialValues.associatedPointValue}
                          onChange={e =>
                            !isBonusPointsReadOnly &&
                            handleChangeValue(
                              e.target.value,
                              'associatedPointValue'
                            )
                          }
                          inputProps={{
                            min: 0,
                            readOnly: isBonusPointsReadOnly,
                            disabled: isBonusPointsReadOnly,
                            inputMode: 'numeric',
                            onKeyDown: e =>
                              handleKeyDown(e, 'associatedPointValue'),
                          }}
                        />
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              partialPointsCheckboxes.associatedPointValue
                            }
                            onChange={(e, value) => {}}
                            inputProps={{'aria-label': 'controlled'}}
                          />
                        }
                        label="Partial points / Maximum daily points achieved"
                      />
                    </Box>
                  )}
                  {memberPointsSummary !== null && (
                    <Box
                      sx={theme => ({
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: colors.cyan[100],
                        mt: `var(--spacing-y, ${theme.spacing(6)})`,
                        mb: `calc(var(--spacing-y, ${theme.spacing(6)}) * -1)`,
                        mx: `calc(var(--spacing-x, ${theme.spacing(4)}) * -1)`,
                        p: 4,
                        borderBottomLeftRadius: `var(--border-radius, ${theme.spacing(
                          2
                        )})`,
                        borderBottomRightRadius: `var(--border-radius, ${theme.spacing(
                          2
                        )})`,
                        gap: theme.spacing(1),
                      })}
                    >
                      <Avatar
                        src={images.icon_membership_points}
                        sx={theme => ({
                          width: theme.spacing(4),
                          height: theme.spacing(4),
                        })}
                      />
                      <Typography
                        sx={{fontSize: 11, fontWeight: 400, lineHeight: 1}}
                      >
                        Member’s approved points for this date:{' '}
                        {memberPointsSummary.approvedPoints}
                      </Typography>
                    </Box>
                  )}
                </ModalInfoBox>
              </>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  value={getKeysSelectedValue(
                    keysSelected['receiptPartnerAndAddress']
                  )}
                  checked={getKeysSelectedValue(
                    keysSelected['receiptPartnerAndAddress']
                  )}
                  onChange={(e, value) =>
                    keysSelected['receiptPartnerAndAddress']?.type === 'force'
                      ? undefined
                      : handleChangeKeysSelected(
                          value,
                          'receiptPartnerAndAddress'
                        )
                  }
                  inputProps={{'aria-label': 'controlled'}}
                />
              }
              label="Island Partner Name/Address/Operating Area"
            />
            {getKeysSelectedValue(keysSelected['receiptPartnerAndAddress']) && (
              <>
                <ModalInfoBox>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      mb: 4.5,
                    }}
                  >
                    Details detected on receipt
                  </Typography>
                  {renderLabelTextWithValue(
                    'Island Partner:',
                    transactionDetail?.islandPartnerName ??
                      NON_PARTICIPATING_OUTLET_LABEL,
                    {
                      renderValueConfig: {
                        wrapInTag: true,
                        tagStyles: {
                          fontWeight: 'normal',
                        },
                      },
                    }
                  )}
                  {renderLabelTextWithValue(
                    'Name:',
                    receiptDetail?.merchantName,
                    {
                      renderValueConfig: {
                        wrapInTag: true,
                        tagStyles: {
                          fontWeight: 'normal',
                        },
                      },
                    }
                  )}
                  {renderLabelTextWithValue(
                    'Address:',
                    receiptDetail?.receiptAddress,
                    {
                      renderValueConfig: {
                        wrapInTag: true,
                        tagStyles: {
                          fontWeight: 'normal',
                        },
                      },
                    }
                  )}
                  {renderLabelTextWithValue(
                    'Operating Area:',
                    receiptDetail?.operatingArea,
                    {
                      renderValueConfig: {
                        wrapInTag: true,
                        tagStyles: {
                          fontWeight: 'normal',
                        },
                      },
                    }
                  )}

                  <Divider
                    sx={{
                      mb: 4.5,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      mb: 4.5,
                    }}
                  >
                    New Island Partner details
                  </Typography>

                  {renderLabelText('Island Partner')}
                  <FormControl fullWidth>
                    <Select
                      labelId="matchedMerchantID"
                      displayEmpty
                      variant="outlined"
                      onChangeSelect={e =>
                        handleChangeValue(e.target.value, 'matchedMerchantID')
                      }
                      value={initialValues.matchedMerchantID}
                      placeholder="Select new Island Partner"
                      size="small"
                      selectOptions={listMerchant}
                      error={errorInputFields.matchedMerchantID !== ''}
                      helperText={errorInputFields.matchedMerchantID}
                    />
                  </FormControl>

                  {initialValues.matchedMerchantID && (
                    <>
                      <Typography
                        sx={{
                          fontSize: 13,
                          mt: 4,
                        }}
                      >
                        To ensure that future similar receipts are catalogued
                        correctly in the OCR database, please select the Island
                        Partner’s name, address, and operating area that match
                        the receipt.
                      </Typography>
                      {renderLabelText('Name', {
                        sx: {
                          mt: 4,
                        },
                      })}
                      <FormControl fullWidth>
                        <Select
                          labelId="receiptName"
                          displayEmpty
                          variant="outlined"
                          onChangeSelect={e =>
                            handleChangeValue(e.target.value, 'receiptName')
                          }
                          value={receiptNameValue}
                          placeholder="Select new Island Partner Name"
                          size="small"
                          selectOptions={islanderPartnerNames}
                          error={errorInputFields.receiptName !== ''}
                          helperText={errorInputFields.receiptName}
                        />
                      </FormControl>

                      {renderLabelText('Address', {
                        sx: {
                          mt: 4,
                        },
                      })}
                      <FormControl fullWidth>
                        <Select
                          disabled={islanderPartnerAddress.length === 0}
                          labelId="receiptAddress"
                          displayEmpty
                          variant="outlined"
                          onChangeSelect={e =>
                            handleChangeValue(e.target.value, 'receiptAddress')
                          }
                          value={receiptAddressValue}
                          placeholder="Select new Island Partner Address"
                          size="small"
                          selectOptions={islanderPartnerAddress}
                          error={errorInputFields.receiptAddress !== ''}
                          helperText={errorInputFields.receiptAddress}
                        />
                      </FormControl>

                      {renderLabelText('Operating Area', {
                        sx: {
                          mt: 4,
                        },
                      })}
                      <FormControl fullWidth>
                        <Select
                          disabled={islanderPartnerOperatingArea.length === 0}
                          labelId="receiptOperatingArea"
                          displayEmpty
                          variant="outlined"
                          onChangeSelect={e =>
                            handleChangeValue(
                              e.target.value,
                              'receiptOperatingArea'
                            )
                          }
                          value={receiptOperatingAreaValue}
                          placeholder={
                            initialValues.merchantReceiptId
                              ? islanderPartnerOperatingArea.length > 0
                                ? 'Select new Island Partner Operating Area'
                                : '--'
                              : 'Select new Island Partner Operating Area'
                          }
                          size="small"
                          selectOptions={islanderPartnerOperatingArea}
                          error={errorInputFields.receiptOperatingArea !== ''}
                          helperText={errorInputFields.receiptOperatingArea}
                        />
                      </FormControl>
                    </>
                  )}
                </ModalInfoBox>
                {renderIslanderPartnerNote()}
              </>
            )}
          </FormControl>
        </Grid>
      )}
      {formError !== null && (
        <Box ref={formErrorRef} sx={{mb: 7.5}}>
          <span className="mandatory-label">{formError}</span>
        </Box>
      )}
    </Grid>
  );
};

export default ApproveReceiptForm;
